<script setup>

import Button from "~/components/atoms/Button.vue";
import SideSlider from "~/components/templates/SideSlider.vue";
import SpoilerTabs from "~/components/molecules/SpoilerTabs.vue";
import FilterGrid from "~/components/molecules/FIlterGrid.vue";
import {useRoute} from "vue-router";
import {watch} from "vue";
import {useCustomFetch} from '~/composables/customFetch.js';

const props = defineProps({
  openFilter: Number,
})

const route = useRoute();
const slug = ref(route.params.slug.join("/").trim('/').replace(/\/$/, ''))
const key = ref(Math.random());

const active = ref(false);
const filter = ref([]);
const loading = ref(false);
const emit = defineEmits(['change']);

const getFilters = (fullUrl, retries = 1) => {
  if (loading.value) return

  loading.value = true
  useCustomFetch(fullUrl, {
    query: {
      search: route.query.search,
    }
  }, true).then((response) => {
    filter.value = response?.filter?.filter(item => Array.isArray(item.values) ? item.values.length : Object.values(item.values).length > 0)
    chosenFilters.value = response.chosen_filters

    for (const key in chosenFilters.value) {
      if (Array.isArray(chosenFilters.value[key])) {
        chosenFilters.value[key] = [...new Set(chosenFilters.value[key])];
      }
    }

    slug.value = response.slug
    loading.value = false
    key.value = Math.random()
  }).catch((error) => {
    if (retries > 0) {
      console.error('Repeating...', error);
      getFilters(fullUrl, retries - 1);
    } else {
      console.error('Error after repeating', error);
      loading.value = false;
      throw error;
    }
  });
}

const chosenFilters = ref({});

const getTabSlotName = (index) => {
  return `tab-content-${index}`;
}

const handleClick = (e) => {
  const {name, value} = e

  if (!chosenFilters.value[name]) {
    chosenFilters.value[name] = []
  }

  if (chosenFilters.value[name].includes(value)) {
    chosenFilters.value[name] = chosenFilters.value[name].filter(item => item !== value)
  } else {
    chosenFilters.value[name].push(value)
  }

  if (name === 'size' && chosenFilters.value[name].includes(value)) {
    const currentFilter = document.querySelector(`.filter-grid[data-filter="size"] [data-slug="${value}"]`)

    if (currentFilter) {
      const subGroup = currentFilter.getAttribute('data-sub-group')
      const group = currentFilter.getAttribute('data-group')

      if (subGroup) {
        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => item !== subGroup)
      }

      if (group) {
        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => item !== group)
      }

      if (!subGroup && !group) {
        const groupValues = document.querySelectorAll(`.filter-grid[data-filter="size"] .active[data-group="${value}"]`)
        const values = Array.from(groupValues).map(item => item.getAttribute('data-slug'))

        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => !values.includes(item))
      } else if (group) {
        const groupValues = document.querySelectorAll(`.filter-grid[data-filter="size"] .active[data-group=${group}][data-sub-group="${value}"]`)
        const values = Array.from(groupValues).map(item => item.getAttribute('data-slug'))

        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => !values.includes(item))
      }
    }
  }

  if (chosenFilters.value[name].length === 0) {
    delete chosenFilters.value[name]
  }

  updateBrowserUrl(chosenFilters.value);
}

const clearFilter = () => {
  chosenFilters.value = {}
  updateBrowserUrl(chosenFilters.value);
}

function createUrlFromFilters(filters) {
  let urlSegments = [];

  for (const [key, values] of Object.entries(filters)) {
    if (Array.isArray(values) && Object.values(values).length > 0) {
      urlSegments.push(`${key}=${values.join('_')}`);
    } else if (values && Object.values(values).length > 0) {
      urlSegments.push(`${key}=${values}`);
    }
  }

  return '/' + urlSegments.join('/');
}

function updateBrowserUrl(filters) {
  const url = createUrlFromFilters(filters);
  if (process.client) {
    const urlParams = new URLSearchParams(window.location.search);

    urlParams.delete('page');

    const queryString = urlParams.toString();
    const fullUrl = '/' + slug.value + url + (queryString ? '?' + queryString : '');

    window.history.replaceState(null, '', fullUrl);
    const event = new CustomEvent('urlReplaced', {
      detail: {
        message: '',
      },
    });
    window.dispatchEvent(event);

    getFilters('/products-grid/filter' + fullUrl)

    emit('change', {
      slug: fullUrl,
      chosenFilters: chosenFilters.value
    })
  }
}

watch(() => props.openFilter, () => {
  active.value = true
})

onMounted(() => {
  getFilters(`/products-grid/filter/${slug.value}`)
})

defineExpose({
  handleClick, clearFilter
})
</script>

<template>
  <div class="filter">
    <SideSlider :title="$t('base.filter_action')" @close="active=false" :active="active">
      <SpoilerTabs v-if="filter.length" :classic-separator="false" :arrows="false"
                   :tabs="filter.map(filterItem => ({title:filterItem.title, opened:filterItem.opened}))">
        <template v-slot:[getTabSlotName(index)] v-for="(filterItem,index) in filter">
          <div class="filter-block" :class="[filterItem.slug, {'text':filterItem.is_text}]">
            <FilterGrid :chosenFilters="chosenFilters" :key="key" @click="handleClick" :filter-item="filterItem"/>
          </div>
        </template>
      </SpoilerTabs>
    </SideSlider>
  </div>
</template>

<style scoped lang="scss">
button {
  width: 139px !important;
  height: 48px !important;
  font-size: 16px;
  position: fixed;
  left: -46px;
  top: 358px;
  transform: rotate(-90deg);
  z-index: 99;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
  letter-spacing: .96px;
  gap: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter {
  &:deep(.tab) {
    height: fit-content !important;

    .heading, .tab-content {
      padding: 0 24px;
    }

    .heading {
      text-transform: uppercase;
    }
  }

  .filter-block {
    max-height: 420px;
    overflow-y: auto;
    gap: 18px;
    margin-left: -10px;
    padding-bottom: 20px;

    &.text {
      margin-left: 0;
    }
  }
}
</style>
